import { useState } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { format, startOfDay } from 'date-fns';
import ToolBar from '@/components/ToolBar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import FormFieldComponent from '@/components/FormFieldComponent';
import FormFieldCalendar from '@/components/FormFieldCalendar';
import FormFieldTextArea from '@/components/FormFieldTextArea';
import { displayError } from '@/lib/error';
import { isAdminOrSupervisor, isDueDiligenceTeamMember } from '@/services/userService';
import { useUserStore } from '@/stores/user';
import { useDDIWorkOrderDetail, useCreateDdiSearchResult, useDdiSearchResult } from '@/services/dueDiligenceService';
import { SSN_SEARCH_DDI_TYPE } from '@/utils/constant';
import { ddiSsnSearchResultSchema, type DdiSsnSearchResultFormValues } from './ddiSearchResult.schema';

const disableFutureDates = (date: Date) => startOfDay(date) > startOfDay(new Date());

export const DDISearchResultCreate = () => {
  const { id: workOrderId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const loggedInUser = useUserStore((state) => state.user);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: workOrder, isFetching, isError } = useDDIWorkOrderDetail(workOrderId ?? null);
  const { data: existingSearchResult, isFetching: isFetchingSearchResult } = useDdiSearchResult(workOrderId ?? null);
  const createSearchResult = useCreateDdiSearchResult();

  const form = useForm<DdiSsnSearchResultFormValues>({
    resolver: zodResolver(ddiSsnSearchResultSchema),
    mode: 'all',
  });

  const canCreateSearchResult = isDueDiligenceTeamMember(loggedInUser) || isAdminOrSupervisor(loggedInUser);

  const handleBack = () => navigate('/due-diligence');

  const saveHandler = async () => {
    if (!workOrder) return;
    const isValid = await form.trigger();
    if (!isValid) return;

    const values = form.getValues();
    setIsSubmitting(true);
    createSearchResult.mutate(
      {
        workOrderId: workOrder.commonDetails.id,
        payload: {
          date: format(values.date, 'yyyy-MM-dd'),
          ssn: values.ssn,
          dob: format(values.dob, 'yyyy-MM-dd'),
          notes: values.notes ?? null,
        },
      },
      {
        onSuccess: () => {
          toast({ variant: 'default', description: 'Search result created successfully.', duration: 5000 });
          void queryClient.invalidateQueries({ queryKey: ['ddi-search-result', workOrder.commonDetails.id] });
          navigate('/due-diligence');
        },
        onError: (error) => {
          displayError(error, 5000);
        },
        onSettled: () => {
          setIsSubmitting(false);
        },
      }
    );
  };

  if (!canCreateSearchResult) {
    return <Navigate to="/not-found" />;
  }

  if (isFetching || isFetchingSearchResult) {
    return <div className="flex items-center justify-center h-64 text-placeholder text-sm">Loading work order…</div>;
  }

  if (isError || !workOrder) {
    return (
      <div className="flex items-center justify-center h-64 text-error text-sm">
        Failed to load work order. Please go back and try again.
      </div>
    );
  }

  if (workOrder.clientDetails.ddiType !== SSN_SEARCH_DDI_TYPE) {
    return <Navigate to="/due-diligence" />;
  }

  if (existingSearchResult) {
    return <Navigate to="/due-diligence" />;
  }

  const personForInquiry = workOrder.searchDetails.personForInquiry;
  const searcher = workOrder.searchDetails.assignedUser;

  if (!personForInquiry || !searcher) {
    return (
      <div className="flex items-center justify-center h-64 text-error text-sm">
        This work order is missing required data to create a search result. Please go back and try again.
      </div>
    );
  }

  return (
    <div className="w-full scrollbar-hide flex flex-col">
      <ToolBar
        title={`W/O: ${workOrder.commonDetails.workOrderNumber} - Add Search Results`}
        secondaryTitle={<span className="text-sm text-placeholder">SSN Search</span>}
      />

      <FormProvider {...form}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2 flex flex-col gap-1">
            <Label className="flex items-center gap-1 text-sm font-medium">
              Person for Inquiry
              <span className="text-error align-top">*</span>
            </Label>
            <Input value={personForInquiry} disabled className="bg-white" />
          </div>

          <FormFieldCalendar<DdiSsnSearchResultFormValues>
            control={form.control}
            name="date"
            label="Date"
            required
            disabledAt={disableFutureDates}
            placeholder="Select Date"
          />

          <FormFieldComponent<DdiSsnSearchResultFormValues>
            control={form.control}
            name="ssn"
            label="SSN"
            required
            placeholder="Enter SSN"
            replaceRegex={/[^0-9-]/g}
          />

          <FormFieldCalendar<DdiSsnSearchResultFormValues>
            control={form.control}
            name="dob"
            label="DOB"
            required
            disabledAt={disableFutureDates}
            placeholder="Select DOB"
          />

          <div className="sm:col-span-2">
            <FormFieldTextArea<DdiSsnSearchResultFormValues>
              name="notes"
              label="Notes"
              placeholder="Enter notes"
              rows={4}
            />
          </div>

          <div className="space-y-2 flex flex-col gap-1">
            <Label className="flex items-center gap-1 text-sm font-medium">
              Searcher
              <span className="text-error align-top">*</span>
            </Label>
            <Input value={searcher.name} disabled className="bg-white" />
          </div>
        </div>
      </FormProvider>

      <div className="flex justify-end mt-4 mb-8 w-full gap-2">
        <Button
          variant="outline"
          className="text-primary py-2 px-4 rounded-md border-primary flex justify-center w-[125px] h-[48px] hover:bg-[#EDF5FF]"
          onClick={handleBack}
        >
          Back
        </Button>
        <Button
          className="text-[#fff] py-2 px-4 rounded-md bg-primary w-[125px] h-[48px] border-primary flex justify-center hover:bg-[#12375C] hover:text-white disabled:bg-primary"
          onClick={saveHandler}
          disabled={isSubmitting}
        >
          Save
        </Button>
      </div>
    </div>
  );
};
